import React from "react";
import { Link } from "react-router-dom";
import { routeSeo } from "./routeSeo";
import siteConfig from "./siteConfig";
import "./breadcrumbs.css";

/**
 * Visible breadcrumb trail, driven by the same route table that generates the
 * BreadcrumbList JSON-LD in scripts/seo-normalize.js.
 *
 * WHY VISIBLE AND NOT JUST MARKUP
 *
 * Google's structured data policy requires breadcrumb markup to correspond to
 * breadcrumbs a user can actually see. Emitting the JSON-LD alone is markup
 * describing content that is not on the page -- the same class of defect as
 * hidden FAQ answers. It also genuinely helps: a crawler or answer engine
 * reading the page gets an explicit statement of where it sits in the site.
 *
 * Renders nothing when the route has no trail defined (the homepage, utility
 * pages), because a one-item breadcrumb is fabricated hierarchy.
 */
export default function Breadcrumbs({ route, items }) {
  // `items` lets pages whose trail is not known at build time supply it
  // directly -- blog articles, whose title comes from the CMS. Without this the
  // build injects BreadcrumbList markup for 205 article URLs that have no
  // visible trail, which is markup describing content that is not on the page.
  const crumbs = items || (routeSeo[route] && routeSeo[route].breadcrumbs);
  if (!crumbs || crumbs.length === 0) return null;

  // Mirrors the JSON-LD builder exactly: Home first, consecutive duplicates
  // collapsed, current page last and not a link.
  const trail = [{ name: "Home", path: "/" }, ...crumbs].filter(
    (item, i, arr) => i === 0 || item.path !== arr[i - 1].path
  );

  return (
    <nav className="breadcrumbNav" aria-label="Breadcrumb">
      <ol className="breadcrumbList">
        {trail.map((item, i) => {
          const isLast = i === trail.length - 1;
          return (
            <li key={`${item.path}-${i}`} className="breadcrumbItem">
              {isLast ? (
                <span aria-current="page">{item.name}</span>
              ) : (
                <>
                  <Link to={siteConfig.canonicalFor(item.path).replace(siteConfig.origin, "") || "/"}>
                    {item.name}
                  </Link>
                  <span className="breadcrumbSep" aria-hidden="true">
                    /
                  </span>
                </>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
